/**
 * Villa Property Type Handler
 */

import { PropertyType } from '../../../shared/definitions/property';
import { ResidentialPropertyHandler } from '../base/residential-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class VillaHandler extends ResidentialPropertyHandler {
  readonly propertyType = PropertyType.Villa;
  readonly displayName = 'Villa';
  readonly pluralName = 'Villas';
  readonly icon = 'villa';
  readonly description = 'Luxury detached villa or estate';

  // Villas have the same rooms as houses
  getRoomTypes(): RoomTypeConfig[] {
    const coreRooms = this.getCoreResidentialRoomTypes();
    
    // Add the same additional rooms as houses
    const villaRooms: RoomTypeConfig[] = [
      {
        typeKey: 'living_room',
        label: 'Living Room',
        pluralLabel: 'Living Rooms',
        countField: 'livingRooms',
        icon: 'weekend',
        defaultCount: 0,
        description: 'Main living and entertainment space',
        formFields: [
          { name: 'name', label: 'Room Name', type: 'text', placeholder: 'e.g., Main Living Room' },
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', min: 10, max: 200 },
          { name: 'hasFireplace', label: 'Has Fireplace', type: 'checkbox', default: false },
          { name: 'hasBalconyAccess', label: 'Balcony Access', type: 'checkbox', default: false },
          { name: 'notes', label: 'Notes', type: 'textarea' }
        ]
      },
      {
        typeKey: 'kitchen',
        label: 'Kitchen',
        pluralLabel: 'Kitchens',
        countField: 'kitchens',
        icon: 'kitchen',
        defaultCount: 0,
        description: 'Kitchen and cooking area',
        formFields: [
          { name: 'name', label: 'Kitchen Name', type: 'text', placeholder: 'e.g., Main Kitchen' },
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', min: 5, max: 100 },
          { name: 'kitchenType', label: 'Kitchen Type', type: 'select', required: true, options: [
            { value: 'open', label: 'Open Kitchen' },
            { value: 'semi-open', label: 'Semi-Open' },
            { value: 'closed', label: 'Closed Kitchen' }
          ]},
          { name: 'isEquipped', label: 'Fully Equipped', type: 'checkbox', default: false },
          { name: 'hasIsland', label: 'Has Island', type: 'checkbox', default: false },
          { name: 'notes', label: 'Notes', type: 'textarea' }
        ]
      }
    ];

    return [...coreRooms, ...villaRooms];
  }

  // Villas inherit house features and add luxury features
  override getFeatures(): FeatureConfig[] {
    const coreFeatures = this.getCoreResidentialFeatures();
    
    const luxuryFeatures: FeatureConfig[] = [
      {
        key: 'wine_cellar',
        label: 'Wine Cellar',
        icon: 'wine_bar',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Climate-controlled wine cellar',
        detailsInterface: 'WineCellarDetail',
        formFields: [
          { name: 'capacity', label: 'Bottle Capacity', type: 'number', suffix: 'bottles', min: 50 },
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', min: 5 },
          { name: 'temperatureControlled', label: 'Temperature Controlled', type: 'checkbox', default: true },
          { name: 'humidityControlled', label: 'Humidity Controlled', type: 'checkbox', default: true }
        ]
      },
      {
        key: 'home_theater',
        label: 'Home Theater',
        icon: 'theaters',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Dedicated home cinema room',
        detailsInterface: 'HomeTheaterDetail',
        formFields: [
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', min: 15 },
          { name: 'numberOfSeats', label: 'Seating Capacity', type: 'number', min: 2, max: 20 },
          { name: 'screenSize', label: 'Screen Size', type: 'number', suffix: 'inches', min: 80 },
          { name: 'soundSystemIncluded', label: 'Sound System Included', type: 'checkbox', default: false },
          { name: 'projectorIncluded', label: 'Projector Included', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'gym',
        label: 'Private Gym',
        icon: 'fitness_center',
        category: FeatureCategory.FLEXIBLE,
        description: 'Private fitness room or gym',
        detailsInterface: 'GymDetail',
        formFields: [
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', min: 10 },
          { name: 'equipmentIncluded', label: 'Equipment Included', type: 'checkbox', default: false },
          { name: 'hasMirrors', label: 'Full Wall Mirrors', type: 'checkbox', default: false },
          { name: 'hasAirConditioning', label: 'Air Conditioning', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'sauna',
        label: 'Sauna',
        icon: 'hot_tub',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Private sauna or steam room',
        detailsInterface: 'SaunaDetail',
        formFields: [
          { name: 'saunaType', label: 'Type', type: 'select', options: [
            { value: 'dry', label: 'Dry Sauna' },
            { value: 'steam', label: 'Steam Room' },
            { value: 'infrared', label: 'Infrared' }
          ]},
          { name: 'capacity', label: 'Capacity', type: 'number', suffix: 'persons', min: 1, max: 12 },
          { name: 'hasShower', label: 'Adjacent Shower', type: 'checkbox', default: false }
        ]
      }
    ];

    // Add house-specific features
    const houseFeatures: FeatureConfig[] = [
      {
        key: 'basement',
        label: 'Basement',
        icon: 'stairs',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Underground basement space',
        detailsInterface: 'BasementDetail',
        formFields: [
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', required: true, min: 5 },
          { name: 'heightCm', label: 'Height', type: 'number', suffix: 'cm', min: 150, max: 400 },
          { name: 'hasWindows', label: 'Has Windows', type: 'checkbox', default: false },
          { name: 'hasElectricity', label: 'Electricity', type: 'checkbox', default: false },
          { name: 'hasHeating', label: 'Heating', type: 'checkbox', default: false },
          { name: 'isFinished', label: 'Finished/Habitable', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'attic',
        label: 'Attic',
        icon: 'roofing',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'Attic or loft space',
        detailsInterface: 'AtticDetail',
        formFields: [
          { name: 'sqm', label: 'Surface Area', type: 'number', suffix: 'm²', required: true, min: 5 },
          { name: 'heightCm', label: 'Height', type: 'number', suffix: 'cm', min: 100, max: 400 },
          { name: 'isConvertible', label: 'Convertible to Living Space', type: 'checkbox', default: false },
          { name: 'hasWindows', label: 'Has Windows', type: 'checkbox', default: false },
          { name: 'hasElectricity', label: 'Electricity', type: 'checkbox', default: false }
        ]
      }
    ];

    return [...coreFeatures, ...houseFeatures, ...luxuryFeatures];
  }

  override getRecommendedMinSurface(): number {
    return 200;
  }

  override getPriceRangeMultiplier(): number {
    return 2.0; // Villas are premium properties
  }
}

